import type { KeyboardEvent } from 'react';
import { useState } from 'react';
import { Input } from '@ooh/ui/components/input';
import { Label } from '@ooh/ui/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ooh/ui/components/select';
import type {
  ClientOfferReviewPageSize,
  ClientOfferReviewPageSizeLabels,
} from './client-offer-review-types';

const PAGE_SIZE_PRESETS: ClientOfferReviewPageSize[] = [20, 50, 100, 200, 'all'];

/**
 * "Na stronie" toolbar control — a preset select (20/50/100/200/Wszystkie)
 * plus a custom-count input, both setting how many rows each page of the
 * numbered pagination bar (bottom of the list) holds. Extracted from
 * `client-offer-review-toolbar.tsx` to keep that function under the
 * `max-lines-per-function` lint limit.
 */
export function ClientOfferReviewPageSizeControl({
  pageSize,
  onPageSize,
  labels,
}: {
  pageSize: ClientOfferReviewPageSize;
  onPageSize: (size: ClientOfferReviewPageSize) => void;
  labels: ClientOfferReviewPageSizeLabels;
}) {
  const [customPageSize, setCustomPageSize] = useState('');

  function submitCustomPageSize(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter') return;
    // Validate the floored integer, not the raw input — a fractional value
    // like "0.5" passes `n > 0` before flooring but produces 0 rows after
    // (`Math.floor(0.5) === 0`). Only clear the input on a successful
    // apply so an invalid value (empty, "0", negative, non-numeric) stays
    // visible instead of silently vanishing with no feedback.
    const n = Math.floor(Number(customPageSize));
    if (!Number.isFinite(n) || n <= 0) return;
    onPageSize(n);
    setCustomPageSize('');
  }

  return (
    <div className="flex items-center gap-1.5">
      <Label htmlFor="co-page-size-select" className="whitespace-nowrap text-xs">
        {labels.pageSizeLabel}
      </Label>
      <Select
        value={String(pageSize)}
        onValueChange={(v) => onPageSize(v === 'all' ? 'all' : Number(v))}
      >
        <SelectTrigger
          id="co-page-size-select"
          className="h-9 w-20 rounded-lg text-xs"
          aria-label={labels.pageSizeAriaLabel}
          title={labels.pageSizeAriaLabel}
          data-testid="page-size-select"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {/*
           * Typing a custom count (below) can set `pageSize` to a value
           * outside the fixed presets — render it as an extra option so the
           * trigger still shows the active number instead of going blank
           * (Radix Select only renders a label for a value that matches one
           * of its `SelectItem`s).
           */}
          {!PAGE_SIZE_PRESETS.includes(pageSize) ? (
            <SelectItem value={String(pageSize)}>{pageSize}</SelectItem>
          ) : null}
          {PAGE_SIZE_PRESETS.map((size) => (
            <SelectItem key={String(size)} value={String(size)}>
              {size === 'all' ? labels.pageSizeAllOption : size}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        value={customPageSize}
        onChange={(e) => setCustomPageSize(e.target.value)}
        onKeyDown={submitCustomPageSize}
        type="number"
        min={1}
        placeholder={labels.pageSizeCustomPlaceholder}
        aria-label={labels.pageSizeCustomAriaLabel}
        title={labels.pageSizeCustomAriaLabel}
        className="h-9 w-20 rounded-lg text-xs"
        data-testid="page-size-custom-input"
      />
    </div>
  );
}
